import type { SampleGroupShape } from '../recipes/recipeForm.ts'

import type { GroupAssignment } from './patientChambers.ts'

/**
 * # 🔴 임시 데이터 — 서버가 이 축을 줄 때까지만 산다
 *
 * **eric 지시 2026-09-21**: 「환자1 — 챔버 1,2 / 환자2 — 챔버 3,4 이렇게 … 환자 단위로 잘라서
 * 그림을 그려줘도 좋을 거 같아.」 그런데 **「어느 시료가 어느 묶음인가」가 서버에 없다.**
 * 그림을 세우려면 그 한 축을 임시로 채워야 한다.
 *
 * ## 무엇을 채우나 — **단 하나다**
 *
 * | 채우는 값 | 서버의 어느 칸이어야 하나 | 왜 서버가 못 주나 |
 * | --- | --- | --- |
 * | 시료 ↔ 레시피 묶음 대응 | `Sample.sample_ref`(레시피 `tray_path.sample_groups[].sample_ref` 와 짝) | **그 칸이 없다.** 배치 생성이 `sample_ids[]` 만 받아 배정을 받을 자리조차 없다(BR-31) |
 *
 * 🔴 **근거(2026-09-21 실측).** 도는 배치 `05c55618` 의 레시피는 묶음을 정확히 적는다 —
 *    `{role:'patient', wells:['A1','A2'], sample_ref:'P1'} … {role:'reference', wells:['C3'],
 *    sample_ref:'REF'}`. 그런데 그 `sample_ref` 는 **레시피 안의 표지**이고 `GET /samples` 의
 *    시료(`FE-0921-H1-04`)에는 그것과 이을 칸이 **없다.** `current_position` 도 0건이다.
 *    ⇒ 두 쪽 다 실물인데 **잇는 줄만 없다.**
 *
 * ## 규율 — 임시인 것이 임시인 채로 남게 한다
 *
 * ⚠ **2026-09-22부터 임시 축은 이 파일 하나뿐이다.** 단계 판정 목(`temporaryStageVerdicts.ts`)은
 *   실물이 부정하는 실패를 그려서 걷었다 — 판정은 `stageVerdicts.ts` 가 서버에서만 읽는다.
 *
 * 1. **있는 축의 빈 값만 채운다.** 웰 이름·역할·묶음 수는 **레시피 원문**이고 여기서 만들지
 *    않는다. 이 파일이 만드는 것은 **대응 하나**뿐이다
 * 2. **결정적이다.** 같은 시료·같은 레시피면 언제나 같은 자리다(난수 없음 · 정렬 하나)
 * 3. **버리지 않는다.** 묶음보다 시료가 많으면 남는 시료가 **제 카드로** 서고 챔버 자리에만
 *    「챔버 배정 없음」이 적힌다. 반대로 묶음이 남으면 그 묶음이 「배정 정보 없음」이 된다
 * 4. **끈 상태에서도 그림이 선다.** 이 함수를 안 넘기면 묶음 구성이 서고(시료 이름 자리는 비고)
 *    시료는 **챔버 없는 카드**로 따로 선다 — 잇는 줄만 없을 뿐 시료도 단계도 서버가 준 사실이다.
 *    그것이 곧 **지금 서버가 말할 수 있는 전부**다(`temporarySampleGroups.test.ts` 가 잰다)
 *
 * ## 🔴 걷어내는 법 — **이 파일 하나를 지우면 끝난다**
 *
 * 모델도 화면도 이 파일을 모른다. `useSampleFlow` 가 `assign` 한 줄로 끼워 넣을 뿐이다.
 * 서버가 대응을 싣기 시작하면(BR-31 되살리기 순서 ⓑ·ⓒ):
 *   1. `assign: temporarySampleGroups` 를 서버 값 읽기로 바꾼다
 *   2. 이 파일과 `temporarySampleGroups.test.ts` 의 목 부분을 지운다
 */

/**
 * 순서 대응 — **가장 자연스러운 임시 규칙**이고, 그 이상을 지어내지 않는다.
 *
 * - 환자 시료를 `sample_id` 순으로 세워 **환자 묶음에 차례로**
 * - 대조 시료를 **대조 묶음에** 차례로
 * - 남는 쪽은 채우지 않는다(묶음이 남으면 `undefined` · 시료가 남으면 **제 카드로** 선다)
 *
 * ⚠ 줄 순서는 부르는 쪽이 이미 `sortFlowRows` 로 세워 넘긴다(격자와 **같은 순서**). 여기서
 *   다시 정렬하면 두 곳이 갈릴 수 있어 **일부러 정렬하지 않는다.**
 */
export const temporarySampleGroups: GroupAssignment = (groups, rows) => {
  const queue = {
    patient: [...rows.filter((r) => !r.reference)],
    reference: [...rows.filter((r) => r.reference)],
  }

  /*
   * 🔴 **남는 시료를 여기서 세지 않는다**(2026-09-21 정정). 모델이 「묶음에 안 들어간 줄」로
   *    직접 셈한다 — 그래야 목이 시료를 흘려도 그 시료의 카드가 화면에서 사라지지 않는다.
   */
  return groups.map((group: SampleGroupShape) => queue[group.role].shift()?.sampleId)
}

/**
 * 🔴 **임시로 채운 값의 수 — 시험이 이 수를 잰다.**
 *
 * 🔴 **지금 임시 축은 이것 하나다**(2026-09-22 — 단계 판정 목을 걷어 둘에서 하나로 줄었다).
 * 인구조사 자리는 `temporarySampleGroups.test.ts` 의 「임시 축 인구조사」다. 서버가 주기
 * 시작하면 이 배열이 비고, 비는 날 이 파일이 사라진다.
 */
export const TEMPORARY_MOCK_FIELDS: readonly string[] = ['sample.sample_ref']
